// packages/core/src/srs/gamification.ts
// ─────────────────────────────────────────────────────────────────────────
// XP, levels, streaks. Pure logic — caller persists via StorageAdapter.
//
// STREAKS (launch model):
//   • A streak means "you showed up to learn today." The web reward
//     dispatcher decides WHICH actions qualify; this module only owns the
//     day math.
//   • The learning DAY flips at 5:00 PM UTC (see streakDayISO below),
//     not at midnight — one deterministic boundary for every user, and
//     the boundary every surface labels.
//   • Streak Freezes: a freeze protects one missed day and applies
//     automatically on the next study day. Freezes CHAIN across multi-day
//     gaps up to the owned count (2 freezes can bridge a weekend away).
//     If the gap is bigger than the freezes owned, the streak resets and
//     the freezes are KEPT — consuming them on an already-broken streak
//     would be petty. Max inventory: MAX_STREAK_FREEZES.
//   • The freeze PRICE lives in economy/EarningRules.ts
//     (STREAK_FREEZE_COST) — price is economy, capacity is streak.
// ─────────────────────────────────────────────────────────────────────────

export interface StreakData {
  current: number;
  longest: number;
  lastStudyDate: string | null;   // ISO date 'YYYY-MM-DD'
  /** Streak Freezes owned (0..MAX_STREAK_FREEZES). Auto-consumed by
   *  applyStudyToday when they fully bridge a gap of missed days. */
  freezesOwned: number;
}

/** Maximum Streak Freezes a user can hold at once. */
export const MAX_STREAK_FREEZES = 2;

export interface LevelInfo {
  level: number;
  xpIntoLevel: number;
  xpNeeded: number;
  progressPct: number;
}

// XP required to advance level N → N+1 (index = level - 1). Extended to a
// 15-level ladder; levels past the table repeat the last threshold.
const THRESHOLDS = [
  100, 150, 200, 250, 300, 350, 400, 500, 600, 750, 900, 1100, 1300, 1500,
  1750,
];

function thresholdFor(level: number): number {
  if (level - 1 < THRESHOLDS.length) return THRESHOLDS[level - 1];
  return THRESHOLDS[THRESHOLDS.length - 1];
}

export function getLevelInfo(totalXP: number): LevelInfo {
  let level = 1;
  let remaining = totalXP;
  while (remaining >= thresholdFor(level)) {
    remaining -= thresholdFor(level);
    level += 1;
  }
  const xpNeeded = thresholdFor(level);
  return {
    level,
    xpIntoLevel: remaining,
    xpNeeded,
    progressPct: Math.round((remaining / xpNeeded) * 100),
  };
}

const LEVEL_LABELS = [
  'Initiate',     // 1
  'Apprentice',   // 2
  'Student',      // 3
  'Adept',        // 4
  'Builder',      // 5
  'Strategist',   // 6
  'Operator',     // 7
  'Capitalist',   // 8
  'Architect',    // 9
  'Master',       // 10
  'Veteran',      // 11
  'Sage',         // 12
  'Vanguard',     // 13
  'Sovereign',    // 14
  'Free',         // 15
];

export function levelLabel(level: number): string {
  if (level <= LEVEL_LABELS.length) return LEVEL_LABELS[level - 1];
  return `Tier ${level}`;
}

/** One rung of the Level Ladder (titles only; XP amounts stay private). */
export interface LevelLadderEntry {
  level: number;
  title: string;
}

/** The full titled ladder, in order, for the Level Ladder surface. */
export function levelLadder(): LevelLadderEntry[] {
  return LEVEL_LABELS.map((title, i) => ({ level: i + 1, title }));
}

// ── The learning day ───────────────────────────────────────────────────
// One product-wide day boundary: a new day starts at 5:00 PM UTC (17:00Z),
// NOT at UTC midnight. Streaks, Daily Review clears, and the daily login
// reward all flip together at that moment, and the UI labels it exactly as
// "5:00 PM UTC". Implementation: shift "now" back by the rollover hours and
// take the UTC calendar date, so day D runs [D 17:00Z, D+1 17:00Z).
//
// Transition note: moving the boundary from midnight can only MERGE two
// adjacent midnight-days into one learning day (never split one day into a
// gap), so existing streaks cannot break when this ships.
export const STREAK_DAY_ROLLOVER_UTC_HOUR = 17;

/** ISO 'YYYY-MM-DD' key of the current learning day (5:00 PM UTC rollover). */
export function streakDayISO(now: Date = new Date()): string {
  return new Date(now.getTime() - STREAK_DAY_ROLLOVER_UTC_HOUR * 3_600_000)
    .toISOString()
    .slice(0, 10);
}

function todayISO(): string {
  return streakDayISO();
}

/** Whole days between two ISO 'YYYY-MM-DD' dates (b - a), UTC-safe. */
function daysBetweenISO(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map((n) => parseInt(n, 10));
  const [by, bm, bd] = b.split('-').map((n) => parseInt(n, 10));
  const aMs = Date.UTC(ay, am - 1, ad);
  const bMs = Date.UTC(by, bm - 1, bd);
  return Math.round((bMs - aMs) / 86_400_000);
}

/**
 * Defensive parse for a persisted streak record. Older records predate
 * `freezesOwned` — they normalize to 0. Garbage normalizes to emptyStreak().
 * Freezes clamp to [0, MAX_STREAK_FREEZES].
 */
export function normalizeStreak(raw: unknown): StreakData {
  if (typeof raw !== 'object' || raw === null) return emptyStreak();
  const r = raw as Partial<StreakData>;
  const current =
    typeof r.current === 'number' && Number.isFinite(r.current)
      ? Math.max(0, Math.floor(r.current))
      : 0;
  const longest =
    typeof r.longest === 'number' && Number.isFinite(r.longest)
      ? Math.max(current, Math.floor(r.longest))
      : current;
  const lastStudyDate =
    typeof r.lastStudyDate === 'string' ? r.lastStudyDate : null;
  const freezesOwned =
    typeof r.freezesOwned === 'number' && Number.isFinite(r.freezesOwned)
      ? Math.min(MAX_STREAK_FREEZES, Math.max(0, Math.floor(r.freezesOwned)))
      : 0;
  return { current, longest, lastStudyDate, freezesOwned };
}

/**
 * Pure function: given current streak state and today's date, return the
 * updated streak. Caller persists. `todayIso` is injectable for tests.
 *
 * Gap handling:
 *   gap 1 (consecutive)        → current + 1
 *   gap > 1, missed ≤ freezes  → freezes consumed (one per missed day),
 *                                current + 1 — the streak never broke
 *   gap > 1, missed > freezes  → current resets to 1, freezes are KEPT
 */
export function applyStudyToday(
  streak: StreakData,
  todayIso: string = todayISO(),
): StreakData {
  if (streak.lastStudyDate === todayIso) return streak;

  // First study day ever.
  if (streak.lastStudyDate === null) {
    return {
      current: 1,
      longest: Math.max(1, streak.longest),
      lastStudyDate: todayIso,
      freezesOwned: streak.freezesOwned,
    };
  }

  const gap = daysBetweenISO(streak.lastStudyDate, todayIso);

  // Clock skew / backwards date: keep the streak, just stamp today.
  if (gap <= 0) {
    return { ...streak, lastStudyDate: todayIso };
  }

  let newCurrent: number;
  let newFreezes = streak.freezesOwned;

  if (gap === 1) {
    newCurrent = streak.current + 1;
  } else {
    const missed = gap - 1;
    if (missed <= streak.freezesOwned) {
      newFreezes = streak.freezesOwned - missed;
      newCurrent = streak.current + 1;
    } else {
      newCurrent = 1; // broken — freezes deliberately kept
    }
  }

  return {
    current: newCurrent,
    longest: Math.max(newCurrent, streak.longest),
    lastStudyDate: todayIso,
    freezesOwned: newFreezes,
  };
}

export function emptyStreak(): StreakData {
  return { current: 0, longest: 0, lastStudyDate: null, freezesOwned: 0 };
}
